import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Power, X } from "lucide-react";
import useServerTable from "../hooks/useServerTable";
import stitchingOperationApi from "../api/stitchingOperationApi";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Input from "../components/Input";
import Modal from "../components/Modal";
import Select2 from "../components/Select2";
import T from "../theme/colors";

const th = {
  padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 800,
  textTransform: "uppercase", letterSpacing: "0.03em", color: T.text2,
  whiteSpace: "nowrap",
};
const td = { padding: "11px 14px", fontSize: 13, color: T.text0, verticalAlign: "top" };

/**
 * Stitching Operation master.
 *
 * An operation ("Collar Attach") holds one or more TYPES ("Single Needle",
 * "Double Needle"). Both are edited in one modal and saved together, because
 * they are one thing to the operator.
 */
export default function StitchingOperationPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [modal, setModal] = useState(null);   // { mode, row }

  const t = useServerTable(stitchingOperationApi.paged, {
    defaultSortBy: "sortOrder",
    defaultSortDir: "asc",
    defaultSize: 25,
    extraFilters: {
      q: search.trim() || undefined,
      isActive: status === "" ? undefined : status === "active",
    },
  });
  const rows = t.items || [];

  const doToggle = async (row) => {
    try {
      await stitchingOperationApi.toggle(row.id);
      t.reload();
    } catch (e) {
      alert(e?.response?.data?.message || "Could not change the status.");
    }
  };

  const doDelete = async (row) => {
    if (!window.confirm(
      `Delete ${row.operationName}?\n\nIt is hidden rather than erased, so past `
      + `production that used it stays readable.`)) return;
    try {
      await stitchingOperationApi.delete(row.id);
      t.reload();
    } catch (e) {
      alert(e?.response?.data?.message || "Could not delete this operation.");
    }
  };

  return (
    <div>
      <PageHeader
        title="Stitching Operations"
        subtitle="Operations and the types beneath them"
        breadcrumb={["Masters", "Stitching Operations"]}
        actions={
          <Btn variant="primary" icon={<Plus size={14} />}
            onClick={() => setModal({ mode: "create" })}>
            New Operation
          </Btn>
        }
      />

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 12 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search operation, code or type…"
          style={{
            flex: "1 1 300px", maxWidth: 420, padding: "8px 12px", borderRadius: 7,
            border: `1px solid ${T.border}`, background: T.bgCard, color: T.text0,
            fontSize: 13, outline: "none", boxSizing: "border-box",
          }} />
        <div style={{ minWidth: 150 }}>
          <Select2 value={status} onChange={setStatus} placeholder="All statuses"
            options={[{ value: "", label: "All statuses" },
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" }]} />
        </div>
      </div>

      <Card style={{ marginTop: 12 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: T.bgInput }}>
                <th style={{ ...th, width: 70, textAlign: "center" }}>Order</th>
                <th style={th}>Code</th>
                <th style={th}>Operation</th>
                <th style={th}>Types</th>
                <th style={{ ...th, textAlign: "center" }}>Status</th>
                <th style={{ ...th, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {t.loading && (
                <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: T.text2, padding: 30 }}>
                  Loading…
                </td></tr>
              )}
              {!t.loading && rows.length === 0 && (
                <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: T.text2, padding: 40 }}>
                  {search.trim() || status
                    ? "No operations match this search."
                    : "No stitching operations yet."}
                </td></tr>
              )}
              {!t.loading && rows.map(r => (
                <tr key={r.id} style={{ borderTop: `1px solid ${T.border}` }}>
                  <td style={{ ...td, textAlign: "center", color: T.text2 }}>{r.sortOrder ?? 0}</td>
                  <td style={{ ...td, fontFamily: "monospace", fontSize: 12 }}>
                    {r.operationCode || "—"}
                  </td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{r.operationName}</div>
                    {r.description && (
                      <div style={{ fontSize: 11, color: T.text2 }}>{r.description}</div>
                    )}
                  </td>
                  <td style={td}>
                    {/* Types listed inline rather than as a count: which types an
                        operation has is the whole reason to open this master. */}
                    {r.types && r.types.length > 0 ? (
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {r.types.map(tp => (
                          <span key={tp.id} style={{
                            fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                            background: tp.isActive === false ? T.bgInput : T.purpleBg,
                            color: tp.isActive === false ? T.text2 : T.purple,
                            textDecoration: tp.isActive === false ? "line-through" : "none",
                          }}>{tp.typeName}</span>
                        ))}
                      </div>
                    ) : <span style={{ color: T.text2 }}>—</span>}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 4,
                      background: r.isActive ? T.successBg : T.bgInput,
                      color: r.isActive ? T.success : T.text2,
                    }}>{r.isActive ? "ACTIVE" : "INACTIVE"}</span>
                  </td>
                  <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>
                    <button onClick={() => setModal({ mode: "edit", row: r })} title="Edit"
                      style={iconBtn(T.warn)}><Pencil size={14} /></button>
                    <button onClick={() => doToggle(r)} title={r.isActive ? "Deactivate" : "Activate"}
                      style={iconBtn(T.info)}><Power size={14} /></button>
                    <button onClick={() => doDelete(r)} title="Delete"
                      style={iconBtn(T.danger)}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {t.totalElements > 0 && (
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "10px 14px", borderTop: `1px solid ${T.border}`, fontSize: 12.5, color: T.text2,
          }}>
            <span>{t.totalElements} operation{t.totalElements === 1 ? "" : "s"}</span>
            <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Btn size="sm" variant="ghost" disabled={t.page <= 1}
                onClick={() => t.setPage(t.page - 1)}>Prev</Btn>
              <span>Page {t.page} of {t.totalPages || 1}</span>
              <Btn size="sm" variant="ghost" disabled={t.page >= (t.totalPages || 1)}
                onClick={() => t.setPage(t.page + 1)}>Next</Btn>
            </span>
          </div>
        )}
      </Card>

      {modal && (
        <OperationModal mode={modal.mode} row={modal.row}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); t.reload(); }} />
      )}
    </div>
  );
}

const iconBtn = (color) => ({
  border: "none", background: "none", cursor: "pointer", color,
  padding: "2px 5px", verticalAlign: "middle",
});

/**
 * Create / edit one operation and its types.
 *
 * The types carry their `id` back to the server so it can match and update them
 * in place. Dropping the ids would make every save delete and recreate the rows,
 * which breaks anything downstream that points at a type.
 */
function OperationModal({ mode, row, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [operationName, setOperationName] = useState(row?.operationName || "");
  const [operationCode, setOperationCode] = useState(row?.operationCode || "");
  const [description, setDescription] = useState(row?.description || "");
  const [sortOrder, setSortOrder] = useState(String(row?.sortOrder ?? 0));
  const [types, setTypes] = useState(
    row?.types?.length
      ? row.types.map(t => ({ ...t }))
      : [{ typeName: "", typeCode: "", isActive: true }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const setType = (i, patch) =>
    setTypes(p => p.map((t, k) => (k === i ? { ...t, ...patch } : t)));
  const addType = () =>
    setTypes(p => [...p, { typeName: "", typeCode: "", isActive: true }]);
  const removeType = (i) => setTypes(p => p.filter((_, k) => k !== i));

  const save = async () => {
    if (!operationName.trim()) { setErr("Operation name is required"); return; }
    // Rows left blank are dropped rather than rejected - adding a row and
    // changing your mind should not block the save.
    const clean = types
      .filter(t => (t.typeName || "").trim())
      .map((t, i) => ({
        id: t.id,
        typeName: t.typeName.trim(),
        typeCode: (t.typeCode || "").trim() || undefined,
        sortOrder: i,
        isActive: t.isActive !== false,
      }));
    const names = clean.map(t => t.typeName.toLowerCase());
    if (new Set(names).size !== names.length) {
      setErr("Two types have the same name");
      return;
    }
    setBusy(true); setErr("");
    try {
      const body = {
        operationName: operationName.trim(),
        operationCode: operationCode.trim() || undefined,
        description: description.trim() || undefined,
        sortOrder: Number(sortOrder) || 0,
        types: clean,
      };
      if (isEdit) await stitchingOperationApi.update(row.id, body);
      else await stitchingOperationApi.create(body);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.message || "Could not save this operation.");
    }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} width={620}
      title={isEdit ? `Edit ${row.operationName}` : "New Stitching Operation"}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
        <Input label="Operation Name *" value={operationName} onChange={setOperationName}
          placeholder="Collar Attach" />
        <Input label="Code" value={operationCode} onChange={setOperationCode}
          placeholder="OP-COL" />
        <Input label="Order" type="number" value={sortOrder} onChange={setSortOrder} />
      </div>
      <Input label="Description" value={description} onChange={setDescription}
        placeholder="Optional" />

      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginTop: 16, marginBottom: 6,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 800, textTransform: "uppercase",
          letterSpacing: "0.03em", color: T.text2,
        }}>Operation Types</span>
        <Btn size="sm" variant="outline" icon={<Plus size={12} />} onClick={addType}>
          Add Type
        </Btn>
      </div>

      <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
        {types.length === 0 && (
          <div style={{ padding: 18, textAlign: "center", fontSize: 12.5, color: T.text2 }}>
            No types yet — an operation can be saved without them.
          </div>
        )}
        {types.map((t, i) => (
          <div key={i} style={{
            display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 8,
            alignItems: "center", padding: "8px 10px",
            borderTop: i === 0 ? "none" : `1px solid ${T.border}`,
          }}>
            <input value={t.typeName || ""} placeholder="Single Needle"
              onChange={e => setType(i, { typeName: e.target.value })}
              style={rowInput} />
            <input value={t.typeCode || ""} placeholder="Code"
              onChange={e => setType(i, { typeCode: e.target.value })}
              style={rowInput} />
            <button type="button" onClick={() => removeType(i)} title="Remove"
              style={{ border: "none", background: "none", cursor: "pointer", color: T.danger }}>
              <X size={15} />
            </button>
          </div>
        ))}
      </div>

      {err && <div style={{ color: T.danger, fontSize: 13, marginTop: 10 }}>{err}</div>}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
        <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={save} loading={busy}>{isEdit ? "Save Changes" : "Create"}</Btn>
      </div>
    </Modal>
  );
}

const rowInput = {
  padding: "6px 9px", borderRadius: 6, fontSize: 13, width: "100%",
  border: `1px solid ${T.border}`, background: T.bgCard, color: T.text0,
  outline: "none", boxSizing: "border-box",
};
